import React, { useState } from 'react';
import { useStaffRoles } from '../../db/hooks/useLookupData';
import { useDatabase } from '../../db/DatabaseProvider';
import { useAuthStore } from '../../stores';
import { now } from '../../lib/uuid';
import { useHasPermission } from '../auth/RequirePermission';
import { useToast } from '../common/ToastProvider';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { Pencil, Trash2, Check, X, Plus } from 'lucide-react';
import { Button } from '../common/Button';

export function StaffRolesManager() {
  const { roles, isLoading, addRole } = useStaffRoles();
  const db = useDatabase();
  const user = useAuthStore((s) => s.user);
  const hasPermission = useHasPermission();
  const canManageConfig = hasPermission('settings:manage');
  const toast = useToast();

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => Promise<void>;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: async () => {},
  });

  // Local state for additions & editing
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [newRole, setNewRole] = useState('');
  const [editingRole, setEditingRole] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRole.trim()) return;
    try {
      await addRole(newRole.trim());
      toast.success('Rol creado exitosamente');
      setNewRole('');
      setIsFormOpen(false);
    } catch (err: any) {
      toast.error('Error al agregar rol: ' + err.message);
    }
  };

  const handleEditStart = (roleName: string) => {
    setEditingRole(roleName);
    setEditName(roleName);
  };

  const handleEditSave = async (oldName: string) => {
    if (!db || !canManageConfig) return;
    const trimmed = editName.trim();
    if (!trimmed || trimmed === oldName) {
      setEditingRole(null);
      return;
    }
    const normalizedNew = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);

    if (roles.includes(normalizedNew)) {
      toast.warning('Ese name de rol ya existe.', 'Rol Duplicado');
      return;
    }

    const affectedStaff = await db.staff.find({
      selector: { role: oldName, is_deleted: { $ne: true } }
    }).exec();

    const affectedNames = affectedStaff.map((s) => s.name).join(', ');
    const confirmMsg = affectedStaff.length > 0
      ? `Hay ${affectedStaff.length} miembros de staff con este role asignado (${affectedNames}). Se cambiará su role de "${oldName}" a "${normalizedNew}" en todos sus perfiles.`
      : `¿Deseas renombrar el role "${oldName}" a "${normalizedNew}"?`;

    setConfirmModal({
      isOpen: true,
      title: 'Renombrar Rol de Personal',
      message: confirmMsg,
      onConfirm: async () => {
        try {
          const doc = await db.app_config.findOne({ selector: { key: 'staff_roles' } }).exec();

          const updatedRoles = roles.map((r) => (r === oldName ? normalizedNew : r));
          if (doc) {
            await doc.patch({
              value: JSON.stringify(updatedRoles),
              updated_at: now(),
              last_modified_by: user?.email || 'system',
            });
          }

          for (const staff of affectedStaff) {
            await staff.patch({
              role: normalizedNew,
              updated_at: now(),
              last_modified_by: user?.email || 'system',
            });
          }

          toast.success('Rol actualizado correctamente');
          setEditingRole(null);
        } catch (err: any) {
          toast.error('Error al renombrar role: ' + err.message);
        } finally {
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        }
      }
    });
  };

  const handleDelete = async (roleName: string) => {
    if (!db || !canManageConfig) return;

    const affectedStaff = await db.staff.find({
      selector: { role: roleName, is_deleted: { $ne: true } }
    }).exec();

    const affectedNames = affectedStaff.map((s) => s.name).join(', ');
    const confirmMsg = affectedStaff.length > 0
      ? `Hay ${affectedStaff.length} miembros de staff con el role "${roleName}" asignado (${affectedNames}). Al eliminar el role, se les quitará esta especialidad (quedando como "Sin role").`
      : `¿Deseas eliminar el role "${roleName}" de las opciones disponibles?`;

    setConfirmModal({
      isOpen: true,
      title: 'Eliminar Rol de Personal',
      message: confirmMsg,
      onConfirm: async () => {
        try {
          const doc = await db.app_config.findOne({ selector: { key: 'staff_roles' } }).exec();

          const updatedRoles = roles.filter((r) => r !== roleName);
          if (doc) {
            await doc.patch({
              value: JSON.stringify(updatedRoles),
              updated_at: now(),
              last_modified_by: user?.email || 'system',
            });
          }

          for (const staff of affectedStaff) {
            await staff.patch({
              role: null,
              updated_at: now(),
              last_modified_by: user?.email || 'system',
              
            });
          }

          toast.success('Rol eliminado correctamente');
        } catch (err: any) {
          toast.error('Error al eliminar rol: ' + err.message);
        } finally {
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        }
      }
    });
  };

  if (isLoading) return <div className="p-6 text-center text-xs text-text-tertiary">Cargando catálogo de roles...</div>;

  return (
    <div className="border border-border bg-surface rounded-2xl shadow-xs overflow-hidden">
      {/* Harmonized Header Bar */}
      <div className="flex justify-between items-center px-6 py-4 border-b border-border bg-surface-secondary/40">
        <div>
          <h3 className="text-sm font-bold text-text-primary">Roles de Personal</h3>
          <p className="text-xs text-text-secondary mt-0.5">Catálogo personalizable de cargos y especialidades</p>
        </div>
        {canManageConfig && (
          <Button variant="primary" size="sm" icon={Plus} onClick={() => setIsFormOpen((prev) => !prev)}>
            {isFormOpen ? 'Cerrar Formulario' : 'Nuevo Rol'}
          </Button>
        )}
      </div>

      {/* Expandable Creation Form */}
      {isFormOpen && canManageConfig && (
        <form onSubmit={handleAdd} className="p-5 border-b border-border bg-surface-secondary/50 space-y-4 animate-fade-in">
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1">Nombre del Rol / Cargo *</label>
            <input
              required
              type="text"
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              className="w-full h-10 rounded-xl border border-border bg-surface px-3.5 text-sm text-text-primary focus:border-brand-sky focus:ring-1 focus:ring-brand-sky transition-all shadow-xs"
              placeholder="Ej. Terapeuta, Recepcionista, Especialista Láser..."
            />
          </div>
          <div className="flex justify-end gap-2 pt-1 border-t border-border/40">
            <Button variant="ghost" size="sm" type="button" onClick={() => setIsFormOpen(false)}>
              Cancelar
            </Button>
            <Button variant="primary" size="sm" type="submit">
              Guardar Rol
            </Button>
          </div>
        </form>
      )}

      {/* Table List */}
      <div>
        <table className="w-full text-left text-sm border-collapse">
          <thead className="bg-surface-secondary/60 text-xs uppercase text-text-tertiary border-b border-border">
            <tr>
              <th className="px-6 py-3 font-semibold">Nombre del Rol</th>
              <th className="px-6 py-3 font-semibold text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {roles.map((roleName) => (
              <tr key={roleName} className="hover:bg-surface-hover/30 transition-colors">
                <td className="px-6 py-3.5 font-bold text-text-primary">
                  {editingRole === roleName ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="h-9 rounded-xl border border-border bg-surface px-3 text-sm text-text-primary focus:border-brand-sky focus:ring-1 focus:ring-brand-sky shadow-xs"
                      />
                      <Button variant="success" size="xs" icon={Check} onClick={() => handleEditSave(roleName)} aria-label="Guardar" />
                      <Button variant="ghost" size="xs" icon={X} onClick={() => setEditingRole(null)} aria-label="Cancelar" />
                    </div>
                  ) : (
                    roleName
                  )}
                </td>
                <td className="px-6 py-3.5 text-right">
                  {canManageConfig && editingRole !== roleName && (
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="xs" icon={Pencil} onClick={() => handleEditStart(roleName)} aria-label="Renombrar" />
                      <Button variant="danger" size="xs" icon={Trash2} onClick={() => handleDelete(roleName)} aria-label="Eliminar" />
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {roles.length === 0 && (
              <tr>
                <td colSpan={2} className="px-6 py-8 text-center text-text-tertiary text-xs">No hay roles registrados</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant="danger"
        confirmText="Confirmar"
      />
    </div>
  );
}
